/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BaseStepCompProps } from '../types';
import G17Client from '../../../services/G17Client';
import LoadingBar from '../partials/LoadingBar';
import { SettingStorage } from '../../../services/SettingStorage';
import { useAppSelector } from '../../../reducers';
import { isStaff, isUserActivated } from '../../../selectors/user';
import { AppCode } from '../../../types/app';
import { AppIds } from '../../../utils/permission-groups';
import { BasicAlert } from '@g17eco/molecules/alert';
import { CompanyInfoFormProps } from '@features/onboarding/hooks/useGetCompanyInfo';

type CompanyInfoProps = Pick<
  BaseStepCompProps,
  'updateForm' | 'formData' | 'handleNext' | 'handlePrevious' | 'appConfig'
> & { renderForm: (props: CompanyInfoFormProps) => React.ReactNode };

const getStorageKey = (appConfigCode: AppCode) => {
  switch (appConfigCode) {
    case AppCode.MaterialityTracker:
      return AppIds.MaterialityTracker;
    default:
      return AppIds.CompanyTracker;
  }
}

export const CompanyInfo = (props: CompanyInfoProps) => {
  const { formData, handleNext, appConfig, renderForm } = props;

  const onboardingPath = appConfig.onboardingPath;
  const isActivated = useAppSelector(isUserActivated);
  const isUserStaff = useAppSelector(isStaff);
  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isActivated && !isUserStaff) {
      // Skip this step if already activated,
      // except if Staff to allow creating companies
      // without needing to create a new user
      handleNext();
    }
  }, [isActivated, isUserStaff, handleNext]);

  const handleCreateCompany = useCallback(async () => {
    setErrorMessage('')
    setSubmitting(true)
    try {
      const { survey } = await G17Client.createCompanyWithSurvey(formData, onboardingPath);

      // Refresh user to get new permissions

      // Pre-select organization for new user
      SettingStorage.setItem(getStorageKey(appConfig.code), survey.initiativeId);
      setSubmitting(false)
      handleNext();

      // Reload to get latest initiative tree, root orgs list, current user with permissions
      // I don't like this... maybe should future refresh individual states
      window.location.reload();
    } catch (e) {
      setSubmitting(false)
      setErrorMessage(e.message)
    }
  }, [formData, onboardingPath, setSubmitting, handleNext, setErrorMessage, appConfig.code]);

  if (isSubmitting) {
    return <LoadingBar />;
  }

  return (
    <>
      <BasicAlert type='danger'>{errorMessage}</BasicAlert>
      <div className='d-lg-flex justify-content-between onboarding-form'>
        <aside className='title-container'>
          <div className='h4 text-strong'>Company details</div>
        </aside>
        {renderForm({
          formData: props.formData,
          handleNext: props.handleNext,
          handlePrevious: props.handlePrevious,
          updateForm: props.updateForm,
          handleCreateCompany,
        })}
      </div>
    </>
  );
}
